import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Button, Checkbox, Color, Flex, Scroller, Search, SubgridTable, Text } from "src/elements";
import { Filterable } from "src/components/Filterable/Filterable";
import styled from "styled-components";
import { MerchflowReviewUpdateStep, ProductUpdates, ProductUpdateSwap } from "../store/types";
import { SwapFacingsMode } from "@CommonApi/merchflow/_merchflowId_/flow-update";
import { useTranslation } from "src/i18n";
import { useLoaders } from "src/components/InlineLoader";
import { PageStatus } from "src/components";
import {
  FlowUpdateProduct,
  RouteGetMerchflow_merchflowId_FlowUpdateProducts,
} from "@CommonApi/merchflow/_merchflowId_/flow-update/products";
import { filterProducts } from "../store/utils";
import { Radio } from "src/elements/Radios/components/Radio";

const Label = styled(Flex)`
  background: ${Color.primaryActive};
  color: ${Color.white};
  padding: 8px;
`;
const TextBox = styled(Flex)`
  border: 1px solid ${Color.spaceGray};
  min-height: 36px;
  padding: 8px;
`;

interface Props {
  addableProducts: FlowUpdateProduct[];
  removableProducts: FlowUpdateProduct[];
  setStep: (step: MerchflowReviewUpdateStep) => void;
  setProductUpdates: Dispatch<SetStateAction<ProductUpdates>>;
}

const ALL = "All";

export const Step3ASwapProducts = ({
  addableProducts,
  removableProducts,
  setStep,
  setProductUpdates,
}: Props) => {
  const { hasLoader } = useLoaders();
  const { t } = useTranslation("common");

  const [productsToAdd, setProductsToAdd] = useState<string[]>([]);
  const [productToRemove, setProductToRemove] = useState<string | null>(null);
  const [canSqueezeSwapTo, setCanSqueezeSwapTo] = useState(false);
  const [swapFacingsMode, setSwapFacingsMode] = useState<SwapFacingsMode>("DOS_MOS_BALANCE");
  const [searchToAdd, setSearchToAdd] = useState("");
  const [searchToRemove, setSearchToRemove] = useState("");
  const [filterVariant, setFilterVariant] = useState<string>(ALL);
  const [filterCdt1, setFilterCdt1] = useState<string>(ALL);
  const [filterCdt2, setFilterCdt2] = useState<string>(ALL);

  const isProductsLoading = hasLoader(RouteGetMerchflow_merchflowId_FlowUpdateProducts);
  const isModified = productsToAdd.length > 0 || productToRemove !== null;

  const filteredRemovableProducts = useMemo(
    () => filterProducts(removableProducts, searchToRemove),
    [removableProducts, searchToRemove],
  );

  const searchFilteredAddableProducts = useMemo(
    () => filterProducts(addableProducts, searchToAdd),
    [addableProducts, searchToAdd],
  );

  const variantOptions = useMemo(() => {
    const values = new Set<string>();
    addableProducts.forEach((p) => {
      if (p.variant) values.add(p.variant);
    });
    return [ALL, ...Array.from(values).sort()].map((v) => ({ value: v, label: v }));
  }, [addableProducts]);

  const cdt1Options = useMemo(() => {
    const values = new Set<string>();
    addableProducts.forEach((p) => {
      if (p.cdt1) values.add(p.cdt1);
    });
    return [ALL, ...Array.from(values).sort()].map((v) => ({ value: v, label: v }));
  }, [addableProducts]);

  const cdt2Options = useMemo(() => {
    const values = new Set<string>();
    addableProducts.forEach((p) => {
      if (p.cdt2) values.add(p.cdt2);
    });
    return [ALL, ...Array.from(values).sort()].map((v) => ({ value: v, label: v }));
  }, [addableProducts]);

  const filteredAddableProducts = useMemo(() => {
    return searchFilteredAddableProducts.filter((p) => {
      if (filterVariant !== ALL && p.variant !== filterVariant) return false;
      if (filterCdt1 !== ALL && p.cdt1 !== filterCdt1) return false;
      if (filterCdt2 !== ALL && p.cdt2 !== filterCdt2) return false;
      return true;
    });
  }, [searchFilteredAddableProducts, filterVariant, filterCdt1, filterCdt2]);

  const selectedCodesSet = useMemo(() => new Set(productsToAdd), [productsToAdd]);

  const allFilteredSelected =
    filteredAddableProducts.length > 0 &&
    filteredAddableProducts.every((p) => selectedCodesSet.has(p.productCode));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredCodes = new Set(filteredAddableProducts.map((p) => p.productCode));
      setProductsToAdd((prev) => prev.filter((code) => !filteredCodes.has(code)));
    } else {
      const filteredCodes = filteredAddableProducts.map((p) => p.productCode);
      setProductsToAdd((prev) => {
        const existing = new Set(prev);
        filteredCodes.forEach((c) => existing.add(c));
        return Array.from(existing);
      });
    }
  };

  const toggleProduct = (productCode: string) => {
    setProductsToAdd((prev) =>
      prev.includes(productCode) ? prev.filter((c) => c !== productCode) : [...prev, productCode],
    );
  };

  const onSave = () => {
    const selectedRemove = removableProducts.find((p) => p.productCode === productToRemove)!;
    const selectedAdd = addableProducts.filter((p) => selectedCodesSet.has(p.productCode));

    setProductUpdates((productUpdates) => [
      ...productUpdates,
      {
        type: "swap",
        add: selectedAdd,
        remove: selectedRemove,
        pnlDecision: "DEFAULT",
        storeCodes: [],
        canSqueezeSwapTo,
        swapFacingsMode,
      } satisfies ProductUpdateSwap,
    ]);

    setCanSqueezeSwapTo(false);
    setSwapFacingsMode("DOS_MOS_BALANCE");
    setStep("STEP_3_PRODUCT_REVIEW");
  };

  return (
    <Flex column flexGrow gap="20px">
      <Flex column flexGrow gap="20px">
        <Text color={Color.primary} variant="h5">
          Please select an available product to tbe added and the product you would like it to
          replace
        </Text>

        {isProductsLoading ? (
          <PageStatus icon="spinner" label="Loading available products..." />
        ) : (
          <Flex flexGrow column gap="10px">
            <Flex justify="between">
              <Search
                width="260px"
                search={searchToRemove}
                setSearch={setSearchToRemove}
                placeholder="Search for removable products"
              />

              <Flex column gap="6px" align="end">
                <Search
                  width="260px"
                  search={searchToAdd}
                  setSearch={setSearchToAdd}
                  placeholder="Search for addable products"
                />
                <Flex gap="8px" width="260px" flexWrap="wrap">
                  <Filterable
                    label="Variant"
                    options={variantOptions}
                    value={filterVariant}
                    setValue={setFilterVariant}
                    defaultValue={ALL}
                  />
                  <Filterable
                    label="CDT 1"
                    options={cdt1Options}
                    value={filterCdt1}
                    setValue={setFilterCdt1}
                    defaultValue={ALL}
                  />
                  <Filterable
                    label="CDT 2"
                    options={cdt2Options}
                    value={filterCdt2}
                    setValue={setFilterCdt2}
                    defaultValue={ALL}
                  />
                </Flex>
              </Flex>
            </Flex>

            <Flex flexGrow gap="4px">
              <SubgridTable
                virtualRowHeight={32}
                columns={[
                  {
                    header: "",
                    id: "selected",
                    width: "40px",
                    justify: "center",
                  },
                  {
                    header: "Removables",
                    id: "product_code",
                    renderer: (data) => (
                      <Text variant="small2" lineClamp={1}>
                        {data.row.productCode} - {data.row.name}
                      </Text>
                    ),
                  },
                ]}
                data={filteredRemovableProducts.map((item) => ({
                  ...item,
                  selected: (
                    <Radio
                      value={item.productCode}
                      isChecked={item.productCode === productToRemove}
                      setIsChecked={(productCode) => setProductToRemove(productCode)}
                    />
                  ),
                }))}
              />

              <Flex column width="500px" gap="20px">
                <Flex column gap="5px">
                  <Label>OUT</Label>
                  <TextBox>{productToRemove || ""}</TextBox>
                </Flex>

                <Flex column gap="5px">
                  <Label>IN</Label>
                  <TextBox>{productsToAdd.length === 0 ? "" : productsToAdd.join(", ")}</TextBox>
                </Flex>

                <Flex gap="8px" align="center">
                  <Checkbox isChecked={canSqueezeSwapTo} setIsChecked={setCanSqueezeSwapTo} />
                  <Text variant="small2">Allow squeezing swap-to product</Text>
                </Flex>

                <Flex gap="8px" align="center">
                  <Radio
                    value="MATCH"
                    label={t("swapFacingsModeMatch")}
                    variant="small2"
                    isChecked={swapFacingsMode === "MATCH"}
                    setIsChecked={() => setSwapFacingsMode("MATCH")}
                  />
                </Flex>

                <Flex gap="8px" align="center">
                  <Radio
                    value="DOS_MOS_BALANCE"
                    label={t("swapFacingsModeFree")}
                    variant="small2"
                    isChecked={swapFacingsMode === "DOS_MOS_BALANCE"}
                    setIsChecked={() => setSwapFacingsMode("DOS_MOS_BALANCE")}
                  />
                </Flex>
              </Flex>

              <Scroller gutter="stable both-edges">
                <SubgridTable
                  virtualRowHeight={32}
                  columns={[
                    {
                      header: "",
                      headerRenderer: (
                        <Checkbox
                          isChecked={allFilteredSelected}
                          setIsChecked={() => toggleSelectAll()}
                        />
                      ),
                      id: "selected",
                      width: "40px",
                      justify: "center",
                    },
                    {
                      header: "Addables",
                      id: "product_code",
                      renderer: (data) => (
                        <Text variant="small2" lineClamp={1}>
                          {data.row.productCode} - {data.row.name}
                        </Text>
                      ),
                    },
                  ]}
                  data={filteredAddableProducts.map((item) => ({
                    ...item,
                    selected: (
                      <Checkbox
                        isChecked={productsToAdd.includes(item.productCode)}
                        setIsChecked={() => toggleProduct(item.productCode)}
                      />
                    ),
                  }))}
                />
              </Scroller>
            </Flex>
          </Flex>
        )}
      </Flex>

      <Flex justify="between" gap="10px">
        <Button
          color={isModified ? "red" : "greenSmoke"}
          variant="inverted"
          onClick={() => setStep("STEP_3_PRODUCT_REVIEW")}
        >
          {isModified ? "Cancel" : "Back"}
        </Button>

        <Button isDisabled={productsToAdd.length === 0 || !productToRemove} onClick={onSave}>
          Save
        </Button>
      </Flex>
    </Flex>
  );
};
